import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link /* SIP-541A: Removed useNavigate as it's not used with window.location */ } from 'react-router-dom'; 
import DOMPurify from 'dompurify'; 

import { SEARCH } from '../../queries';
import { CategoriesContext } from '../../contexts/CategoriesContextProvider';
import ChipSet from '../modules/ChipSet';
import Paper from '../modules/Paper';
import { Subtitle, Heading } from '../modules/Typography';
import parseMarkdown from '../../utils/markdownParser';
import { LoadingIcon } from '../modules/Loading';
import { useQuery } from '@apollo/client'; 

const ignoreCategories = ['Integ', 'Disney Streaming'];

const SearchResults = () => {
    const { query } = useParams(); 
    const { categories } = useContext(CategoriesContext);
    // SIP-541B: Removed useNavigate hook initialization

    // SIP-541C: Sanitize the URL query parameter
    const unsanitizedSearchTerm = query ? query.replace(/-/g, ' ') : ''; 
    const sanitizedQuery = DOMPurify.sanitize(unsanitizedSearchTerm).trim();

    // SIP-541D: Effect to redirect to home if search term is effectively empty using pure JS
    useEffect(() => {
        if (!sanitizedQuery) { 
            console.log('Empty or invalid search term detected after sanitization, forcing redirect to home with window.location.');
            window.location.href = '/'; // SIP-541E: Direct JavaScript redirection
        }
    }, [sanitizedQuery]); // navigate is no longer a dependency

    // SIP-541F: If the sanitizedQuery is empty, render nothing or a loading state briefly
    // The useEffect above will handle the actual navigation, so no GraphQL query is made with an empty term.
    if (!sanitizedQuery) {
        return <LoadingIcon />; 
    }

    // SIP-541G: useQuery now depends on the sanitizedQuery.
    const { data, loading, error } = useQuery(SEARCH, {
        variables: { searchTerm: sanitizedQuery }
    });

    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchResults, setSearchResults] = useState([]);
    const [documents, setDocuments] = useState([]);

    useEffect(() => {
        if (data) {
            const _documents = [];

            let i = 0;
            const len = data.documents.data.length;
            for (; i < len; i++) {
                const document = data.documents.data[i];
                const _subcategories = [];
                const jen = document.attributes.subcategories.data.length;
                for (let j = 0; j < jen; j++) {
                    const subcategories = document.attributes.subcategories.data[j];

                    if (
                        !subcategories.attributes.category ||
                        !subcategories.attributes.category.data
                    ) {
                        continue;
                    }
                    if (
                        ignoreCategories.includes(
                            subcategories.attributes.category.data.attributes.name
                        )
                    ) {
                        continue;
                    }

                    _subcategories.push(subcategories);
                }

                if (_subcategories.length > 0) {
                    document.attributes.subcategories = _subcategories;
                    _documents.push(document);
                }
            }

            setSearchResults(_documents);
            setDocuments(_documents);
        }
    }, [data]);

    useEffect(() => {
        if (selectedCategory === 'All') {
            setDocuments(searchResults);
        } else {
            const filteredDocuments = searchResults.filter((document) => {
                return document.attributes.subcategories.find((subcategories) => {
                    return (
                        subcategories.attributes.category.data.attributes.name ===
                        selectedCategory
                    );
                });
            });

            setDocuments(filteredDocuments);
        }
    }, [selectedCategory, searchResults]); 

    if (loading) return <LoadingIcon />;
    if (error) console.error({ error });

    return (
        <div className='Search'>
            <Heading number={5} heavy block className='Document__title'>
                Search Results
            </Heading>
            <Paper elevation={6} className='Search__Paper'>
                <div className='flex Search__Header'>
                    <div>
                        {/* SIP-541H: Display the sanitized query */}
                        <Subtitle block number={3}>
                            Search for "{sanitizedQuery}"
                        </Subtitle>
                        <Subtitle block number={2}>
                            {documents.length} result{documents.length !== 1 ? 's' : ''}
                        </Subtitle>
                    </div>
                    <ChipSet
                        chips={['All', ...categories.map((cat) => cat.attributes.name)]}
                        activeChip={selectedCategory}
                        handleClick={(chip) => setSelectedCategory(chip)}
                    />
                </div>
                <div className='Search__results__container'>
                    {documents &&
                        documents.length !== 0 &&
                        documents.map((doc, idx) => {
                            const { slug, subcategories } = doc.attributes;
                            const subcategory = subcategories[0];
                            const catSlug =
                                subcategory.attributes.category.data.attributes.slug;
                            const subSlug = subcategory.attributes.slug;

                            return (
                                <div className='Search__result' key={`result-${idx}`}>
                                    <Link to={`/${catSlug}/${subSlug}/${slug}`}>
                                        <Subtitle block number={3} color='primary'>
                                            {idx + 1}. {doc.attributes.name}
                                        </Subtitle>
                                    </Link>
                                    <Subtitle block number={2}>
                                        {parseMarkdown(doc.attributes.description)}
                                    </Subtitle>
                                </div>
                            );
                        })}
                </div>
            </Paper>
        </div>
    );
};

export default SearchResults;